import { useState, useCallback, useEffect, useRef } from "react";
// add these at the top
const MAX_TIMEOUT_SECONDS = 30; // if student is away for 30s total, auto-submit

export function useAntiCheat(active, onAutoSubmit) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isViolating, setIsViolating] = useState(false);
  const violations = useRef([]);
  const totalTimeOutMs = useRef(0);

  // ── helpers ───────────────────────────────────────────────────────────────

  const startViolation = useCallback((reason) => {
    setIsViolating(true);
    violations.current.push({
      reason,
      startTime: Date.now(),
      endTime: null,
    });
  }, []);


  const endViolation = useCallback((reason) => {
    const index = [...violations.current]
      .reverse()
      .findIndex(v => v.reason === reason && v.endTime === null);
    if (index === -1) return;
    const realIndex = violations.current.length - 1 - index;
    const violation = violations.current[realIndex];
    violation.endTime = Date.now();
    totalTimeOutMs.current += violation.endTime - violation.startTime;

    // ── auto-submit if total time away exceeds limit ──
    const totalSeconds = Math.round(totalTimeOutMs.current / 1000);
    if (totalSeconds >= MAX_TIMEOUT_SECONDS && onAutoSubmit) {
      onAutoSubmit();
    }

    const anyStillOpen = violations.current.some(v => v.endTime === null);
    setIsViolating(anyStillOpen);
  }, [onAutoSubmit]);

  const clearViolations = useCallback(() => {
    violations.current = [];
    totalTimeOutMs.current = 0;
  }, []);

  // ── 1. Fullscreen ─────────────────────────────────────────────────────────
  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    try {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } catch (err) {
      console.warn("Fullscreen request failed:", err);
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    try {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } catch (err) {
      console.warn("Exit fullscreen failed:", err);
    }
  }, []);

  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
        if (active) startViolation("fullscreen_exit");
      } else {
        setIsFullscreen(true);
        if (active) endViolation("fullscreen_exit");  // returning from fullscreen_exit violation
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [active, startViolation, endViolation]);

  // ── 2. Tab switch ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        startViolation("tab_switch");
      } else {
        endViolation("tab_switch");
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [active, startViolation, endViolation]);

  // ── window blur — alt+tab, OS overlays, notifications ─────────────────────
  useEffect(() => {
    if (!active) return;

    const onBlur = () => {
      // only start if no violation already open (tab_switch may have opened one)
      const last = violations.current[violations.current.length - 1];
      const alreadyOpen = last && last.endTime === null;
      if (!alreadyOpen) {
        startViolation("window_blur");
      }
    };

    const onFocus = () => {
      endViolation("window_blur");
    };

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, [active, startViolation, endViolation]);

  // ── beforeunload warning ───────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";  // triggers browser's native "Leave page?" dialog
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active]);

  // ── keyboard shortcut blocking ────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      const blocked =
        (ctrl && e.key === "c") ||   // copy
        (ctrl && e.key === "v") ||   // paste
        (ctrl && e.key === "u") ||   // view page source
        (ctrl && e.key === "s") ||   // save page
        (ctrl && e.key === "p") ||   // print
        (ctrl && e.key === "r") ||   // refresh
        (ctrl && e.key === "w") ||   // close tab
        (ctrl && e.key === "t") ||   // new tab
        (ctrl && e.key === "n") ||   // new window
        (ctrl && e.key === "Tab") || // switch tab
        e.key === "F5" ||           // refresh
        e.key === "F11" ||           // toggle fullscreen manually
        e.key === "F12";             // devtools

      if (blocked) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [active]);

  return {
    isFullscreen,
    isViolating, clearViolations,
    enterFullscreen, exitFullscreen,
    getViolations: () => violations.current,
    getTotalTimeOutSeconds: () => Math.round(totalTimeOutMs.current / 1000),
  };


}